import { observable, configure, action } from 'mobx';

import util from 'preact-util';

configure({ enforceActions: 'always' });

class DuettStore {
    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @observable clients = [];
    @observable clientsLoading = false;
    @observable articleSetup = null;
    @observable articleSetupLoading = false;
    @observable voucherPreview = null;
    @observable voucherPreviewLoading = false;
    @observable voucherHistory = [];
    @observable voucherHistoryLoading = false;
    @observable voucherPostLoading = false;
    @observable voucherPostResult = null;
    @observable comparisonData = null;
    @observable comparisonLoading = false;

    async getClients() {
        this.updateKeyValue('clientsLoading', true);
        const response = await util.fetchApi('/api/integrations/duett/clients', { method: 'GET' }, {});
        this.updateKeyValue('clientsLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('clients', response.data || []);
                return response;
            default:
                console.error('duettStore.getClients', response);
                return response;
        }
    }

    async setupArticles() {
        this.updateKeyValue('articleSetupLoading', true);
        const response = await util.fetchApi('/api/integrations/duett/setup-articles', { method: 'POST' }, {});
        this.updateKeyValue('articleSetupLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('articleSetup', response.data);
                return response;
            default:
                console.error('duettStore.setupArticles', response);
                this.updateKeyValue('articleSetup', { error: response.message });
                return response;
        }
    }

    async getArticles(articleNumbers) {
        const response = await util.fetchApi('/api/integrations/duett/articles', { method: 'GET' }, {
            articleNumbers: (articleNumbers || []).join(','),
        });
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('duettStore.getArticles', response);
                return response;
        }
    }

    async getCustomers(visitorId) {
        const response = await util.fetchApi('/api/integrations/duett/customers', { method: 'GET' }, { visitorId });
        if (response.status !== 200) console.error('duettStore.getCustomers', response);
        return response;
    }

    async createCustomer(visitorId) {
        const response = await util.fetchApi('/api/integrations/duett/customers', { method: 'POST' }, { visitorId });
        if (response.status !== 200) console.error('duettStore.createCustomer', response);
        return response;
    }

    async createInvoice(saleId, { invoiceSendOption } = {}) {
        if (!saleId) return null;
        const response = await util.fetchApi(`/api/integrations/duett/invoices/${saleId}`, { method: 'POST' }, { invoiceSendOption });
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('duettStore.createInvoice', response);
                return response;
        }
    }

    async simulateInvoice(saleId, { invoiceSendOption } = {}) {
        if (!saleId) return null;
        const response = await util.fetchApi(`/api/integrations/duett/invoices/${saleId}?simulate=true`, { method: 'POST' }, { invoiceSendOption });
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('duettStore.simulateInvoice', response);
                return response;
        }
    }

    async createCreditNote(saleId) {
        if (!saleId) return null;
        const response = await util.fetchApi(`/api/integrations/duett/credit-notes/${saleId}`, { method: 'POST' }, {});
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('duettStore.createCreditNote', response);
                return response;
        }
    }

    async getInvoices({ dateFrom, dateTo } = {}) {
        const response = await util.fetchApi('/api/integrations/duett/invoices', { method: 'GET' }, { dateFrom, dateTo });
        if (response.status !== 200) console.error('duettStore.getInvoices', response);
        return response;
    }

    async updateInvoices(saleIds) {
        const response = await util.fetchApi('/api/integrations/duett/invoices', {
            method: 'PATCH',
        }, {
            saleIds,
        });
        switch (response.status) {
            case 202:
                return response;
            default:
                console.error('duettStore.updateInvoices', response);
                return response;
        }
    }

    async updateInvoicesByDate(dateFrom, dateTo, dryRun = false) {
        const response = await util.fetchApi(`/api/integrations/duett/invoices/by-date?dryRun=${dryRun ? '1' : '0'}`, {
            method: 'PATCH',
        }, {
            dateFrom,
            dateTo,
        });
        switch (response.status) {
            case 202:
                return response;
            default:
                console.error('duettStore.updateInvoicesByDate', response);
                return response;
        }
    }

    async getInvoicesCompare({ dateFrom, dateTo } = {}) {
        this.updateKeyValue('comparisonLoading', true);
        const response = await util.fetchApi('/api/integrations/duett/invoices-compare', {
            method: 'GET',
        }, { dateFrom, dateTo });
        if (response.status === 200) {
            this.updateKeyValue('comparisonData', response.data);
        }
        this.updateKeyValue('comparisonLoading', false);
        return response;
    }

    @action clearComparisonData() {
        this.updateKeyValue('comparisonData', null);
        this.updateKeyValue('comparisonLoading', false);
    }

    async getVoucherPreview({ startDate, endDate, excludeInvoice, excludeImported }) {
        this.updateKeyValue('voucherPreviewLoading', true);
        this.updateKeyValue('voucherPreview', null);
        const params = { startDate, endDate };
        if (excludeInvoice) {
            params.excludeInvoice = '1';
        }
        if (excludeImported) {
            params.excludeImported = '1';
        }
        const response = await util.fetchApi('/api/integrations/duett/voucher-preview', {
            method: 'GET',
        }, params);
        this.updateKeyValue('voucherPreviewLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('voucherPreview', response.data);
                return response;
            default:
                console.error('duettStore.getVoucherPreview', response);
                return response;
        }
    }

    async postVoucher({ startDate, endDate, periodType, excludeInvoice, excludeImported }) {
        this.updateKeyValue('voucherPostLoading', true);
        this.updateKeyValue('voucherPostResult', null);
        const response = await util.fetchApi('/api/integrations/duett/vouchers', {
            method: 'POST',
        }, {
            startDate,
            endDate,
            periodType,
            excludeInvoice,
            excludeImported,
        });
        this.updateKeyValue('voucherPostLoading', false);
        switch (response.status) {
            case 201:
                this.updateKeyValue('voucherPostResult', response);
                this.getVoucherPreview({ startDate, endDate, excludeInvoice, excludeImported });
                this.getVoucherHistory();
                return response;
            default:
                console.error('duettStore.postVoucher', response);
                this.updateKeyValue('voucherPostResult', response);
                return response;
        }
    }

    async getVoucherHistory() {
        this.updateKeyValue('voucherHistoryLoading', true);
        const response = await util.fetchApi('/api/integrations/duett/vouchers');
        this.updateKeyValue('voucherHistoryLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('voucherHistory', response.data || []);
                return response;
            default:
                console.error('duettStore.getVoucherHistory', response);
                return response;
        }
    }

    @action clearVoucherData() {
        this.updateKeyValue('voucherPreview', null);
        this.updateKeyValue('voucherPostResult', null);
    }
}

const store = new DuettStore();
export default store;
